import Styles from './MobilePillEditor.module.scss'
import { Hash2, Trash2 } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react'
import { Drawer } from 'components/Drawer'
import { DrawerProps } from 'components/Drawer/Root'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Button } from 'blaze-ui/Button'
import { nanoid } from 'nanoid'
import { useKeyboardInset } from './useKeyboardInset'
import { Plus1 } from 'blaze-ui/icons/24'
import cx from 'classnames'

export type EditorPill = {
  id: string
  label: string
}

export type MobilePillEditorProps = {
  initiallyEditing?: boolean
  onClose?: () => void
  onUpdate?: (pills: EditorPill[]) => void
  open: boolean
  pills: EditorPill[]
  title: string
  prefix?: string
}

export const MobilePillEditor = (props: MobilePillEditorProps) => {
  const { title, onUpdate, open, onClose, pills, initiallyEditing = false, prefix = '' } = props

  const contentRef = useRef<HTMLDivElement>(null)
  const [draftPills, setDraftPills] = useState<EditorPill[]>(pills)

  const [isEditing, setIsEditing] = useState<boolean>(initiallyEditing)

  const scrollToBottom = useCallback(() => {
    if (!contentRef.current) return
    contentRef.current.scrollTop = contentRef.current.scrollHeight
  }, [])

  const handleInputSubmit = (value: string) => {
    const newPill = { id: nanoid(), label: `${prefix}${value.trim()}` }
    const newPills = [...draftPills, newPill]
    setDraftPills(newPills)
    handleUpdate(newPills)
  }

  const handleDelete = (pill: EditorPill) => {
    const newPills = draftPills.filter((p) => p.id !== pill.id)
    setDraftPills(newPills)
    handleUpdate(newPills)
  }

  useEffect(() => {
    if (!open || !isEditing || draftPills.length === 0) return
    scrollToBottom()
  }, [open, isEditing, scrollToBottom, draftPills])

  const handleUpdate = (pills?: EditorPill[]) => {
    onUpdate?.(pills ?? draftPills)
  }

  return (
    <PillEditorBottomDrawer title={title} open={open} onDone={onClose}>
      <div className={Styles.root}>
        {draftPills.length === 0 ? (
          <div className={Styles.contentEmpty}>
            <Hash2 size={24} color="var(--dark-40)" />
            <Text variant="primary" color="var(--dark-40)">{`No ${title.toLowerCase()}`}</Text>
          </div>
        ) : (
          <div className={cx(Styles.content, { [Styles.editing]: isEditing })} ref={contentRef}>
            {draftPills.map((pill) => (
              <PillRow key={pill.id} pill={pill} onDelete={handleDelete} />
            ))}
          </div>
        )}
        {isEditing ? (
          <PillEditorTextInput onSubmit={handleInputSubmit} onBlur={() => setIsEditing(false)} prefix={prefix} />
        ) : (
          <Button className={Styles.addNewButton} size="xl" onPress={() => setIsEditing(true)} frontIcon={Plus1}>
            Add New
          </Button>
        )}
      </div>
    </PillEditorBottomDrawer>
  )
}

type PillRowProps = {
  pill: EditorPill
  onDelete?: (pill: EditorPill) => void
}

const PillRow = ({ pill, onDelete }: PillRowProps) => {
  return (
    <div className={Styles.pillRowRoot}>
      <Text variant="primary" color="var(--dark-80)">
        {pill.label}
      </Text>
      <div className={Styles.deleteButtonContainer}>
        <Button size="xl" variant="editor" frontIcon={Trash2} rounded onPress={() => onDelete?.(pill)} />
      </div>
    </div>
  )
}

type PillEditorTextInputProps = {
  onSubmit: (value: string) => void
  onBlur: () => void
  prefix?: string
}

const focusInput = (element: HTMLInputElement | null) => {
  if (!element || document.activeElement === element) return
  element.focus()
}

const PillEditorTextInput = ({ onBlur, onSubmit, prefix = '' }: PillEditorTextInputProps) => {
  const [draftPill, setDraftPill] = useState<EditorPill | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const isDraftPillValid = !!draftPill && !!draftPill.label.trim()
  const handleInputSubmit = (e?: React.FormEvent<HTMLFormElement>) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    if (!isDraftPillValid) return
    setDraftPill(null)
    onSubmit(draftPill.label)
    focusInput(inputRef.current)
  }

  useEffect(() => {
    const requestId = requestAnimationFrame(() => focusInput(inputRef.current))
    return () => {
      cancelAnimationFrame(requestId)
    }
  }, [])

  return (
    <div className={Styles.textInputContainer}>
      <form className={Styles.textInputForm} onSubmit={handleInputSubmit}>
        <span className={Styles.inputContainer}>
          {prefix}
          <input
            autoFocus
            className={Styles.textInput}
            name="pill"
            enterKeyHint="done"
            type="text"
            placeholder={`Add new...`}
            ref={inputRef}
            onBlur={(e) => {
              onBlur()
            }}
            value={`${draftPill?.label ?? ''}`}
            onChange={(e) =>
              setDraftPill((current) =>
                current ? { ...current, label: e.target.value } : { id: nanoid(), label: e.target.value?.trim() },
              )
            }
          />
        </span>
      </form>
    </div>
  )
}

interface PillEditorBottomDrawerProps extends DrawerProps {
  title: string
  cancelLabel?: string
  onDone?: () => void
}

const PillEditorBottomDrawer = ({
  title,
  onDone,
  children,
  ...rest
}: PropsWithChildren<PillEditorBottomDrawerProps>) => {
  const inset = useKeyboardInset()

  return (
    <Drawer.Root {...rest}>
      <Drawer.Portal>
        <Drawer.Overlay withBlur />
        <Drawer.Content
          fullHeight
          style={{ height: `100%`, paddingTop: `${inset}px`, transition: 'padding-top 0.3s ease-in-out' }}
        >
          <Drawer.InnerContent>
            <Drawer.Top className={Styles.drawerTop}>
              <Drawer.Title asChild>
                <div className={Styles.drawerTitle}>
                  <Heading lineClamp={1} level={5}>
                    {title}
                  </Heading>
                </div>
              </Drawer.Title>
              <div className={Styles.drawerRightAction}>
                <Button className={Styles.saveButton} variant="ghost" size="lg" rounded onPressStart={onDone}>
                  Done
                </Button>
              </div>
            </Drawer.Top>
            <Drawer.Container className={Styles.drawerContainer}>{children}</Drawer.Container>
          </Drawer.InnerContent>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
}
